import { Minus, Plus } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { getActivePricing, totalsForFamily } from '@/api/billingService';
import { formatEuros, totalAtSeats } from '@/lib/priceBreakdown';
import { cn } from '@/lib/utils';

import { PRECO, VALOR_TOTAL_POR_LOCAL, euros as eurosValor } from './copy';

export const LOCAIS_MIN = 1;
// El mismo tope que el checkout (`BILLING_PRO_MAX_SEATS`): más locales es venta.
export const LOCAIS_MAX = 30;

// El anual de 1 local (tarifa v4): lo que se pinta mientras no llega la tarifa.
const PRECO_1_LOCAL_CENTS = 28700;

/**
 * El precio anual de N locales con los tramos de la tarifa vigente, del mismo
 * `active-pricing` que el resto de superficies de precio (público, sirve con
 * el enlace del correo). Para 1 local es el 287 € de la oferta; mientras la
 * tarifa no ha llegado, o si falla, se pinta ese 287 € de `copy.js`.
 *
 * Trae también el «valor total» tachado de N locales (`VALOR_TOTAL_POR_LOCAL`
 * × N) y el «Poupas» (ese valor menos el precio): con uno o con treinta.
 */
export function usePrecoLocais(locais) {
  const { data: pricing } = useQuery({
    queryKey: ['billing', 'active-pricing'],
    queryFn: getActivePricing,
    staleTime: Infinity,
  });
  const n = Math.max(1, locais || 1);
  const totals = totalsForFamily(pricing, pricing?.family);
  const tarifa = totals?.annual ? totalAtSeats(totals.annual, n) : 0;
  const valorCents = VALOR_TOTAL_POR_LOCAL * n;
  if (!tarifa || n <= 1) {
    const cents = n <= 1 ? PRECO_1_LOCAL_CENTS : 0;
    return {
      varios: false, total: PRECO.preco, porMes: PRECO.porMes, comIva: PRECO.comIva,
      riscado: eurosValor(valorCents),
      poupas: cents ? eurosValor(valorCents - cents) : null,
    };
  }
  const euros = (c) => formatEuros(c, 'pt-PT', c % 100 ? 2 : 0);
  return {
    varios: true,
    total: euros(tarifa),
    porMes: formatEuros(Math.round(tarifa / 12), 'pt-PT'),
    comIva: formatEuros(Math.round(tarifa * 1.23), 'pt-PT'),
    riscado: eurosValor(valorCents),
    poupas: eurosValor(valorCents - tarifa),
  };
}

/**
 * El selector de locales de la landing (con y sin ficha): − número +, de 1
 * a 30, siempre anual. El número no se escribe: es un `input` de solo lectura
 * para que se lea y se anuncie como un valor, y solo cambian los botones.
 *
 * `compacto` es el de la barra fija: botones de 36 px y el rótulo más pequeño
 * (10,5 px), para caber junto al precio y al «Começar» en un móvil de 280 px.
 * El de la tarjeta de precio lleva botones de 44 px. Los dos llevan el mismo
 * rótulo «Número de locais»: sin él, en la barra el número suelto no se sabía
 * de qué era.
 *
 * @param {{ locais: number, setLocais: import('react').Dispatch<import('react').SetStateAction<number>>, escuro?: boolean,
 *           compacto?: boolean, className?: string }} props
 */
export default function SeletorLocais({ locais, setLocais, escuro = false, compacto = false, className }) {
  // Forma funcional: dos clics seguidos antes de repintar suman dos.
  const menos = () => setLocais((n) => Math.max(LOCAIS_MIN, n - 1));
  const mais = () => setLocais((n) => Math.min(LOCAIS_MAX, n + 1));
  const boton = cn(
    'flex flex-shrink-0 items-center justify-center rounded-lg border transition-colors disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#feae2c]',
    compacto ? 'h-9 w-9' : 'h-11 w-11',
    escuro
      ? 'border-white/25 bg-white/10 text-white hover:bg-white/20'
      : 'border-[#e5e5ea] bg-white text-[#1d1d1f] hover:bg-[#f5f5f7]',
  );

  return (
    <div className={cn('flex flex-col', compacto ? 'gap-1' : 'gap-1.5', className)}>
      <span
        className={cn(
          'whitespace-nowrap font-bold uppercase',
          compacto ? 'text-[10.5px] leading-none tracking-[0.4px]' : 'text-xs tracking-[0.6px]',
          escuro ? 'text-[#c7c7cc]' : 'text-[#5e5e66]',
        )}
      >
        Número de locais
      </span>
      <div className="flex items-center gap-1.5" data-testid="seletor-locais">
        <button type="button" onClick={menos} disabled={locais <= LOCAIS_MIN} aria-label="Menos um local" className={boton}>
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
        <input
          type="number"
          readOnly
          tabIndex={-1}
          value={locais}
          min={LOCAIS_MIN}
          max={LOCAIS_MAX}
          aria-label="Número de locais"
          aria-live="polite"
          className={cn(
            'min-w-0 rounded-lg border bg-transparent text-center font-extrabold tabular-nums outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            compacto ? 'h-9 w-9 text-base' : 'h-11 w-14 text-lg',
            escuro ? 'border-white/25 text-white' : 'border-[#e5e5ea] text-[#1d1d1f]',
          )}
        />
        <button type="button" onClick={mais} disabled={locais >= LOCAIS_MAX} aria-label="Mais um local" className={boton}>
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
        {!compacto && (
          <span className={cn('ml-1.5 text-sm', escuro ? 'text-[#c7c7cc]' : 'text-[#5e5e66]')}>
            {locais === 1 ? 'local' : 'locais'}
          </span>
        )}
      </div>
    </div>
  );
}
